import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../../lib/sectionFallbacks/derivedFallbacks';
import {
  EditorialSubsection,
  EditorialRecommendations,
  EditorialInstrumentPanel,
  EditorialHeroMetric,
  EditorialStatusChip,
  type EditorialChipStatus,
} from '../../report/editorial';
import { FindingClaimsList } from '../../report/FindingClaimsList';
import { makeRenderCitedText, resolveBlockScale, emptyStateNoteProps } from './feasibilityBodyHelpers';
import { isEditorialLayoutsEnabled } from '../../../../lib/runtimeFlags';
import { formatFixed } from '../../../../lib/coerceNumber';
import { hasGeneratedEditorialLayer } from '../../../../lib/editorial/resolveEditorialLayer';
import { EditorialProseBody } from '../../report/editorial/EditorialProseBody';

/** Fiber-density → RAG verdict chip (re-skinned to the active preset's
 *  positive/warning/negative roles, NOT a baked color). */
const FIBER_RAG: Record<'High' | 'Medium' | 'Low', EditorialChipStatus> = {
  High: 'green',
  Medium: 'amber',
  Low: 'red',
};

/** Shared sub-heading style for the two folded sub-bodies (Transport / Connectivity). */
function subHeadingStyle(theme: ReturnType<typeof useEffectiveReportTheme>): React.CSSProperties {
  return {
    fontFamily: 'var(--report-body-font, inherit)',
    letterSpacing: '0.14em',
    color: roleColor(theme, 'ink'),
  };
}

/**
 * COMBINES — Logistics & Connectivity (feasibility). MERGES the formerly
 * separate `transportStudy` ("Transport & Logistics") and `connectivityStudy`
 * ("Connectivity & Fiber") sections into ONE section. The router shell owns the
 * single "Logistics & Connectivity" anchor / heading / ProvenanceStrip; this
 * body renders BOTH sub-bodies under labelled <h3> sub-heads so NO content is
 * dropped:
 *
 *   1. Transport & Logistics  — road / rail / port / heavy-haul + transport
 *                               recommendations + confirmed transport risks
 *                               (the verbatim former transportStudy body).
 *   2. Connectivity & Fiber   — fiber / IX / datacenters / latency + the K3
 *                               instrument panel + fiber-density metric +
 *                               connectivity recommendations + confirmed
 *                               connectivity risks (the verbatim former
 *                               connectivityStudy body).
 *
 * The two source FIELDS (`report.transportStudy`, `report.connectivityStudy`)
 * are STILL emitted by the feasibility prompt and the merged renderer consumes
 * both. Each sub-body self-omits when its source field is absent; the section
 * shows the generic insufficient-data note only when BOTH are absent.
 */
export const TransportStudySection: React.FC<{ report: SiteRagReport; label?: string }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const renderCitedText = makeRenderCitedText(report);
  const blockScale = resolveBlockScale(theme.headingScale);

  const transport = report.transportStudy;
  const connectivity = report.connectivityStudy;
  // WAVE 3 — the merged section routes through the single `transportStudy`
  // section id; when a GENERATED layer is present its flowing prose body
  // re-voices the road/rail/port + fiber/IX/latency ASSESSMENTS, so those
  // labelled EditorialSubsection prose blocks are SUPPRESSED. The EXHIBITS
  // (recommendations rails, confirmed risks, the network instrument panel,
  // the fiber-density metric) are KEPT — they carry data the prose cannot.
  const layerPresent = hasGeneratedEditorialLayer(report, 'transportStudy');
  // N1 — in the editorial monograph (gated so flag-OFF is byte-stable), make each
  // 2-col assessment GRID atomic so it never splits mid-row across a page. A CSS
  // grid that fragments mid-row desynced its two column tracks → the left-column
  // prose overlapped the right-column instrument panel once the editorial opener
  // shifted the break point. Each grid is < one page, so moving it whole to the
  // next page (a clean break) is safe — no overlap, no data loss.
  const gridBreak = isEditorialLayoutsEnabled() ? ({ breakInside: 'avoid' as const }) : {};

  // K3 — connectivity instrument readout (verbatim from the former
  // ConnectivityStudySection). Built from the deterministic infrastructure
  // dataset (latency-to-IX + fiber density), so it only renders when present.
  const infra = report.datasets?.infrastructure;
  const latencyMs =
    infra && typeof infra.latency === 'number' && Number.isFinite(infra.latency) && infra.latency > 0
      ? infra.latency
      : null;
  const fiberDensity = infra?.fiberDensity;
  // Latency RAG: ≤5 ms green, ≤15 ms amber, else red (datacenter-grade bands).
  const latencyRag: EditorialChipStatus | null =
    latencyMs == null ? null : latencyMs <= 5 ? 'green' : latencyMs <= 15 ? 'amber' : 'red';

  // The merged section shows the generic insufficient-data note ONLY when BOTH
  // source bodies are absent — never dropping content when one is present.
  if (!transport && !connectivity) {
    return (
      // N1.5C-flat — graceful-omit note flattens to a muted left-rule note on the
      // cream page in editorial mode (shared seam); flag OFF byte-identical.
      <div {...emptyStateNoteProps(isEditorialLayoutsEnabled(), theme, true)} data-testid="transportStudy-empty">
        {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4">
      {/* WAVE 3 prose body */}
      {layerPresent && <EditorialProseBody report={report} sectionId="transportStudy" theme={theme} />}
      {/* ── Sub-body 1: Transport & Logistics (verbatim former transportStudy) ── */}
      {transport && (
        <div className="space-y-3" data-subsection="transportStudy">
          <h3 className="text-[11px] font-semibold uppercase" style={subHeadingStyle(theme)}>Transport &amp; Logistics</h3>
          {/* N1 — the 2-col assessment/recs grid is a TALL MIXED container (prose
              + a recs/risks rail), NOT an atomic exhibit: it must BREAK across a
              page rather than be forced atomic. It already spans both flow columns
              in N2 via `data-span-columns`, so it needs no `data-flow="span"`
              (which would force break-inside:avoid and overflow a page). The
              genuine atomic exhibits inside (instrument panel etc.) carry their
              own span semantics. */}
          <div data-span-columns style={gridBreak} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* N1 — atomic grid columns (see the connectivity sub-body note below):
                break BETWEEN columns, never split a row. */}
            {!layerPresent && (
              <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: blockScale === 'compact' ? 10 : 16 }}>
                <EditorialSubsection theme={theme} label="Road Access">{renderCitedText(transport.roadAccess)}</EditorialSubsection>
                <EditorialSubsection theme={theme} label="Rail Access">{renderCitedText(transport.railAccess)}</EditorialSubsection>
                <EditorialSubsection theme={theme} label="Port / Waterway">{renderCitedText(transport.portAccess)}</EditorialSubsection>
                <EditorialSubsection theme={theme} label="Heavy Haul Route">{renderCitedText(transport.heavyHaulRoute)}</EditorialSubsection>
              </div>
            )}
            <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <EditorialRecommendations
                theme={theme}
                items={((transport.recommendations) || []).map((r) => renderCitedText(r))}
              />
              {(transport.risks?.length ?? 0) > 0 && (
                <FindingClaimsList
                  entries={transport.risks ?? []}
                  references={report.references}
                  siteId={report.siteId}
                  reportId={report.id}
                  sectionId="transportStudy"
                  responses={report.openQuestionResponses}
                  redFlagsHeading="Transport risks (confirmed)"
                  className="space-y-4"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Sub-body 2: Connectivity & Fiber (verbatim former connectivityStudy) ── */}
      {connectivity && (
        <div
          className="space-y-3"
          data-subsection="connectivityStudy"
          // PAGE-5 GHOST-COLUMN FIX — when the merged section carries the FULL
          // labelled assessments (no generated prose layer, so the four Transport
          // AND four Connectivity EditorialSubsections all render) the combined body
          // is ~1 page TALLER than the A4 deck box. The paged deck flows the report
          // frame through pagedjs' fixed-height `column-width` multicol, so that
          // vertical overrun does NOT relocate to the next page — it spills into a
          // far-right SECOND column (~688px at x≈1794) that `.pagedjs_sheet`
          // (overflow:hidden) CLIPS, dropping the tail of the Connectivity prose and
          // tripping the horizontal-overflow gate (page-content scrollWidth ~2482 vs
          // ~688). pagedjs will not fragment the flow `lg:grid` here (break-inside /
          // display:block / atomic-grid / 2-col all leave the spill), so we start the
          // Connectivity sub-body on a FRESH page at the natural Transport|Connectivity
          // topic seam — each sub-body then fits its own page, the multicol never opens
          // a 2nd column, and NO content is clipped. This `data-page-break-before`
          // HOOK is consumed by a DECK stylesheet rule in
          // PaginatedReportView.buildPagedStylesheet (pagedjs honours `break-before`
          // ONLY from its injected stylesheets, NOT from an inline style on the cloned
          // source). Stamped only for the dense case: `!layerPresent` (a generated
          // layer SUPPRESSES the subsections, so the body fits and a forced break
          // would strand half a page) AND `transport` present (so a Transport sub-body
          // precedes it — never orphan a lone heading). Inert outside the paged deck
          // (the marker has no effect on the continuous on-screen viewer), so the
          // flag-OFF / non-deck render is byte-stable.
          data-page-break-before={transport && !layerPresent ? 'true' : undefined}
        >
          <h3 className="text-[11px] font-semibold uppercase" style={subHeadingStyle(theme)}>Connectivity &amp; Fiber</h3>
          {/* N1 — TALL MIXED container (prose + instrument panel + recs/risks):
              spans both flow columns via `data-span-columns` but must BREAK across
              a page (no `data-flow="span"` atomic). */}
          <div data-span-columns style={gridBreak} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Assessment sub-blocks → theme-adaptive EditorialSubsection (no boxy card).
                N1 — keep each grid COLUMN atomic (`breakInside: 'avoid'`) so a 2-col
                CSS grid breaks cleanly BETWEEN its columns across a page rather than
                splitting a grid ROW — which let the left-column prose overlap the
                right-column instrument panel when the editorial opener shifted the
                break point. Each column is ~half a page, so atomic is safe. */}
            {!layerPresent && (
              <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: blockScale === 'compact' ? 10 : 16 }}>
                <EditorialSubsection theme={theme} label="Fiber Infrastructure">{renderCitedText(connectivity.fiberInfrastructure)}</EditorialSubsection>
                <EditorialSubsection theme={theme} label="Internet Exchange">{renderCitedText(connectivity.internetExchange)}</EditorialSubsection>
                <EditorialSubsection theme={theme} label="Data Center Facilities">{renderCitedText(connectivity.datacenters)}</EditorialSubsection>
                <EditorialSubsection theme={theme} label="Latency Analysis">{renderCitedText(connectivity.latencyAnalysis)}</EditorialSubsection>
              </div>
            )}
            <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {latencyMs != null && (
                <EditorialInstrumentPanel
                  theme={theme}
                  heading="Network · Nearest IX"
                  // Match the governed ~10.2 ms ESTIMATED IX-latency value: 1 dp,
                  // not 3 (3 dp implied false precision on an estimate).
                  heroValue={formatFixed(latencyMs, 1)}
                  heroUnit="ms"
                  heroLabel="Latency to Nearest IX"
                  heroTone={latencyRag === 'green' ? 'positive' : latencyRag === 'amber' ? 'warning' : 'negative'}
                  chip={latencyRag ?? undefined}
                  // Label the RAG chip with the METRIC it rates ("IX Latency"),
                  // not the bare verdict word, so it never reads as a single
                  // verdict against the separate Fiber-Density chip below.
                  chipLabel="IX Latency"
                  rows={fiberDensity ? [{ key: 'Fiber density', value: fiberDensity }] : undefined}
                  caveat="Round-trip latency to the nearest internet exchange; verify against carrier route maps."
                />
              )}
              {/* CONNECTIVITY-ADVISORY — 'Unknown' (no connectivity data)
                  renders no RAG chip/hero: an absence is not a tier and must
                  not be colored. The plain row above still shows "Unknown". */}
              {fiberDensity && fiberDensity !== 'Unknown' && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                  <EditorialHeroMetric
                    theme={theme}
                    size="sm"
                    label="Fiber Density"
                    value={fiberDensity}
                    tone={fiberDensity === 'High' ? 'positive' : fiberDensity === 'Low' ? 'negative' : 'warning'}
                  />
                  {/* Label the density RAG chip with its METRIC ("Fiber Density")
                      so it is unambiguous against the "IX Latency" chip above. */}
                  <EditorialStatusChip theme={theme} status={FIBER_RAG[fiberDensity]}>Fiber Density</EditorialStatusChip>
                </div>
              )}
              <EditorialRecommendations
                theme={theme}
                items={((connectivity.recommendations) || []).map((r) => renderCitedText(r))}
              />
              {(connectivity.risks?.length ?? 0) > 0 && (
                <FindingClaimsList
                  entries={connectivity.risks ?? []}
                  references={report.references}
                  siteId={report.siteId}
                  reportId={report.id}
                  sectionId="connectivityStudy"
                  responses={report.openQuestionResponses}
                  redFlagsHeading="Connectivity risks (confirmed)"
                  className="space-y-4"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
